// "Por comisión": lo pagado por cada regla en el periodo, con su peso en el total.
// Mockup: surekeep/mockups/backoffice/portal-admin-vl-com-pagadas.html
import { useState } from 'react';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { CellLines, DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import { MasterDetailEmpty, MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { StatusPill } from '@/shared/patterns/Pills';
import { ToolbarMetric } from '@/shared/patterns/TableToolbar';
import { useRules } from '../../api/commissionQueries';
import { usePayoutRows } from '../../api/usePayoutRows';
import {
    applyRulePill,
    buildRuleRows,
    contributorsNote,
    filterRuleRows,
    nextPaidSort,
    RULE_SORT,
    rulePillCounts,
    sellersAtLeastTitle,
    sellersText,
    sortRows,
    type PaidSort,
    type RulePaidRow,
    type RulePill,
    type RuleSortKey,
} from '../../lib/paidRows';
import { DEFAULT_PAYOUT_PERIOD, periodText } from '../../lib/payoutPeriods';
import type { MatrixGroup } from '../../lib/ruleMatrix';
import { STATUS_LABEL, STATUS_TONE } from '../../lib/ruleText';
import { FIELD, LABEL } from '../fieldStyles';
import {
    PaidDetailUnavailable,
    PaidFilters,
    PaidListFrame,
    type PaidTabProps,
} from './PaidListFrame';
import { PayoutPeriodSelect } from './PayoutPeriodSelect';
import { RulePaidDetail } from './RulePaidDetail';
import { ShareCell } from './ShareCell';

const KIND_OPTIONS: ReadonlyArray<{ value: MatrixGroup | 'all'; label: string }> = [
    { value: 'all', label: 'Todos' },
    { value: 'Bono/venta', label: 'Bono por venta' },
    { value: 'Multiplicador', label: 'Multiplicador' },
    { value: 'Bono umbral', label: 'Bono umbral' },
    { value: 'Renta de cartera', label: 'Renta de cartera' },
];

export function RulesPaidTab({ period, onPeriodChange, today, directory }: PaidTabProps) {
    const rulesQuery = useRules();
    const source = usePayoutRows('RULE', period, today);
    const [search, setSearch] = useState('');
    const [kind, setKind] = useState<MatrixGroup | 'all'>('all');
    const [pill, setPill] = useState<RulePill>('all');
    const [sort, setSort] = useState<PaidSort<RuleSortKey>>({ key: 'paid', direction: 'desc' });
    const [selected, setSelected] = useState<string | null>(null);

    const rules = rulesQuery.data?.rules ?? [];
    const all = buildRuleRows(source.rows, rules, source.sellersAtLeast);
    const filtered = filterRuleRows(all, { search, kind });
    const counts = rulePillCounts(filtered);
    const rows = sortRows(applyRulePill(filtered, pill), sort, RULE_SORT);
    const maxCents = Math.max(0, ...rows.map((row) => row.paidCents));
    const when = periodText(period);
    // Sin regla elegida se abre la primera cuando las fuentes ya respondieron, y queda fijada: un
    // refetch que reordena la tabla no cambia la ficha abierta.
    const firstId = rows.at(0)?.ruleId ?? null;
    if (selected === null && source.isReady && firstId !== null) setSelected(firstId);
    const selectedRow =
        selected === null ? undefined : (all.find((row) => row.ruleId === selected) ?? rows.at(0));

    const columns: DataTableColumn<RulePaidRow, RuleSortKey>[] = [
        {
            key: 'name',
            label: 'Regla',
            sortKey: 'name',
            cell: (row) => <CellLines primary={row.name} secondary={row.kind ?? '—'} />,
        },
        {
            key: 'status',
            label: 'Estado',
            width: '96px',
            cell: (row) =>
                row.status ? (
                    <StatusPill tone={STATUS_TONE[row.status]}>
                        {STATUS_LABEL[row.status]}
                    </StatusPill>
                ) : (
                    '—'
                ),
        },
        {
            key: 'sellers',
            label: 'Vendedores',
            width: '84px',
            numeric: true,
            sortKey: 'sellers',
            cell: (row) => sellersText(row),
            cellTitle: sellersAtLeastTitle,
        },
        {
            key: 'units',
            label: 'Piezas',
            width: '70px',
            numeric: true,
            sortKey: 'units',
            cell: (row) => (row.countsSales ? `${String(row.unitsCount)} vtas` : row.unitsCount),
            cellTitle: (row) => (row.countsSales ? 'La regla cuenta ventas' : undefined),
        },
        {
            key: 'paid',
            label: 'Liquidado · % del total',
            width: '230px',
            sortKey: 'paid',
            note: when,
            cell: (row) => (
                <ShareCell
                    paidCents={row.paidCents}
                    totalCents={source.totalPaidCents}
                    maxCents={maxCents}
                />
            ),
        },
    ];

    const filters = (
        <PaidFilters
            onClear={() => {
                setSearch('');
                setKind('all');
                setPill('all');
                onPeriodChange(DEFAULT_PAYOUT_PERIOD);
            }}
            note="Por regla cuenta lo liberado: bonos con su parte del multiplicador, umbrales y renta. Sin ajustes ni reversas, así que el total no cuadra con las otras pestañas."
        >
            <div>
                <label htmlFor="rules-paid-search" className={LABEL}>
                    Búsqueda
                </label>
                <input
                    id="rules-paid-search"
                    className={FIELD}
                    placeholder="Regla o ID"
                    value={search}
                    onChange={(event) => {
                        setSearch(event.target.value);
                    }}
                />
            </div>
            <PayoutPeriodSelect period={period} onChange={onPeriodChange} today={today} />
            <div>
                <label htmlFor="rules-paid-kind" className={LABEL}>
                    Tipo de regla
                </label>
                <select
                    id="rules-paid-kind"
                    className={FIELD}
                    value={kind}
                    onChange={(event) => {
                        setKind(event.target.value as MatrixGroup | 'all');
                    }}
                >
                    {KIND_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
        </PaidFilters>
    );

    const list = (
        <PaidListFrame
            pills={[
                { key: 'all', label: 'Todas', value: counts.all },
                { key: 'active', label: 'Activas', value: counts.active, tone: 'success' },
                { key: 'inactive', label: 'Sin activaciones', value: counts.inactive },
                { key: 'expired', label: 'Vencidas', value: counts.expired },
            ]}
            pill={pill}
            onPill={setPill}
            pillsLabel="Reglas por actividad"
            metrics={
                <>
                    <ToolbarMetric label="Reglas" value={source.error ? '—' : rows.length} />
                    <ToolbarMetric
                        label={`Liquidado · ${when}`}
                        value={source.error ? '—' : formatMoneyCents(source.totalPaidCents)}
                    />
                </>
            }
            error={source.error}
            errorTitle="No se pudo cargar lo liquidado por regla"
            onRetry={source.refetch}
        >
            <DataTable
                columns={columns}
                rows={rows}
                rowKey={(row) => row.ruleId}
                caption="Comisiones por regla"
                sort={sort}
                onSortToggle={(key) => {
                    setSort(nextPaidSort(sort, key, ['name']));
                }}
                isRowSelected={(row) => row.ruleId === selectedRow?.ruleId}
                onRowClick={(row) => {
                    setSelected(row.ruleId);
                }}
                isLoading={source.isLoading}
                empty="Ninguna regla coincide con los filtros."
                density="compact"
            />
        </PaidListFrame>
    );

    const detail = source.error ? (
        <PaidDetailUnavailable />
    ) : selectedRow ? (
        <RulePaidDetail
            key={selectedRow.ruleId}
            row={selectedRow}
            rule={rules.find((rule) => rule.ruleId === selectedRow.ruleId)}
            totalCents={source.totalPaidCents}
            when={when}
            directory={directory}
            contributorsNote={contributorsNote(source)}
        />
    ) : (
        <MasterDetailEmpty
            title="Selecciona una regla"
            sub="Elige una fila para ver su costo por semana y quién la activó."
        />
    );

    return (
        <MasterDetailExplorer
            filters={filters}
            list={list}
            detail={detail}
            storageKey="comisiones-pagadas-regla"
        />
    );
}
